import { findFencedBlock } from './factory-questions';
import type { MachineReviewers } from './types/factory';

/**
 * T-36: the second-model review of an AI builder PR. A reviewer (Codex, the Cursor CLI, or Claude when
 * Codex built it) reads the PR; the builder judges each finding in its own T3 thread and ends its turn
 * with one fenced block,
 *
 * ```factory-review
 * { "findings": [{ "title": "…", "where": "worker/x.ts:12", "verdict": "fixed", "small": true }] }
 * ```
 *
 * (a disputed finding can name the run's suggestion that is the same thing: `"suggestion": "retry"`),
 * which the flow's `review.verdict` step reads: what was fixed, what isn't real (and why), and what's
 * disputed (a person decides). The task's PR then carries a summary (TaskPrReview) for the card's badge.
 */

export const FACTORY_REVIEW_LANG = 'factory-review';

export const REVIEW_VERDICTS = ['fixed', 'not real', 'disputed'] as const;
export type ReviewVerdict = (typeof REVIEW_VERDICTS)[number];

/**
 * T-75: how many review rounds a run gets. The flows' `reviewRounds` input is `by size` (the default,
 * what the AI builder uses when it picks a task up) or a number. By size: XS none, S and M one, L and
 * XL two, and a task with no size one. With no rounds the PR's checks are still waited for.
 */
export const REVIEW_ROUNDS_INPUT = 'reviewRounds';
export const REVIEW_ROUNDS_BY_SIZE = 'by size';
export const MAX_REVIEW_ROUNDS = 2;

export function reviewRoundsForSize(size: string | null | undefined): number {
  switch (size) {
    case 'XS':
      return 0;
    case 'L':
    case 'XL':
      return 2;
    default:
      return 1;
  }
}

/** The rounds an input value means: a number (capped), or `by size` (and anything else) from the size. */
export function reviewRoundsFor(value: unknown, size: string | null | undefined): number {
  const text = typeof value === 'number' ? String(value) : typeof value === 'string' ? value.trim() : '';
  if (/^\d+$/.test(text)) return Math.min(Number(text), MAX_REVIEW_ROUNDS);
  return reviewRoundsForSize(size);
}

/** "No review", "1 round", "2 rounds". */
export function reviewRoundsText(rounds: number): string {
  return rounds === 0 ? 'No review' : rounds === 1 ? '1 round' : `${rounds} rounds`;
}

/** Most findings read from one block, and characters per piece of text. */
export const FACTORY_REVIEW_MAX_FINDINGS = 30;
const TITLE_MAX = 300;
const WHERE_MAX = 200;
const REASON_MAX = 1000;

/**
 * SF-T-300: what a disputed finding's options mean, in the builder's words, so the person settling it
 * can tell what each one does: the reviewer's worry in plain words, what leaving it means, what the fix
 * would change, an optional third option of the builder's own, and which one it recommends.
 */
export interface DisputeDetail {
  /** What the reviewer is worried about, in plain words. */
  concern?: string;
  /** What leaving it as it is means (what stays, what the risk is). */
  leave?: string;
  /** What the fix would change, and how big it is. */
  fix?: string;
  /** A third option the builder offers (a different fix, a smaller one…); picking it sends it back like a fix. */
  other?: { label: string; detail?: string };
  /** The option the builder recommends. */
  recommend?: DisputeOption;
}

export const DISPUTE_OPTIONS = ['leave', 'fix', 'other'] as const;
export type DisputeOption = (typeof DISPUTE_OPTIONS)[number];

const CONCERN_MAX = 600;
const OPTION_MAX = 500;
const OTHER_LABEL_MAX = 120;

/** A disputed finding's detail from the builder's JSON, checked and trimmed; empty when it gave none. */
export function cleanDisputeDetail(f: Record<string, unknown>): DisputeDetail {
  const concern = text(f.concern, CONCERN_MAX);
  const leave = text(f.leave, OPTION_MAX);
  const fix = text(f.fix, OPTION_MAX);
  const rawOther = (typeof f.other === 'string' ? { label: f.other } : (f.other ?? {})) as Record<string, unknown>;
  const otherLabel = text(rawOther.label ?? rawOther.title, OTHER_LABEL_MAX);
  const otherDetail = text(rawOther.detail ?? rawOther.description, OPTION_MAX);
  const other = otherLabel ? { label: otherLabel, ...(otherDetail ? { detail: otherDetail } : {}) } : undefined;
  const rec = typeof f.recommend === 'string' ? f.recommend.trim().toLowerCase() : '';
  const recommend = rec === 'leave' || rec === 'fix' || (rec === 'other' && other) ? (rec as DisputeOption) : undefined;
  return {
    ...(concern ? { concern } : {}),
    ...(leave ? { leave } : {}),
    ...(fix ? { fix } : {}),
    ...(other ? { other } : {}),
    ...(recommend ? { recommend } : {}),
  };
}

export interface ReviewFinding extends DisputeDetail {
  title: string;
  /** file:line, when the reviewer gave one. */
  where?: string;
  verdict: ReviewVerdict;
  /** Why it isn't real, why it's disputed, or what the fix was. */
  reason?: string;
  /** A fixed finding whose fix was small (a line or two): it needs no second review round. */
  small?: boolean;
  /**
   * SF-T-74: a disputed finding that is the same thing as one of the run's suggestions: that
   * suggestion's id. Deciding the suggestion settles the finding (shared/factory-dispute.ts).
   */
  suggestion?: string;
  /**
   * SF-T-154: on a PR several sub-tasks share, the ref of the sub-task a disputed finding is about
   * ("SF-T-138"), when the builder could tell. The parent's dispute card shows it beside the finding.
   */
  task?: string;
}

export interface FactoryReview {
  findings: ReviewFinding[];
}

const text = (v: unknown, max: number): string => (typeof v === 'string' ? v.replace(/\s+/g, ' ').trim().slice(0, max) : '');

/** SF-T-154: a task ref as a finding names it ("SF-T-138", "T-12"); '' when it isn't one. */
export function taskRef(value: unknown): string {
  const v = typeof value === 'string' ? value.trim().toUpperCase() : '';
  return /^[A-Z][A-Z0-9]{0,9}(-[A-Z][A-Z0-9]{0,9})?-\d{1,7}$/.test(v) ? v : '';
}

/** "fixed", "not real", "disputed", with the spellings an agent might use for them; null otherwise. */
export function normalizeVerdict(value: unknown): ReviewVerdict | null {
  const v = typeof value === 'string' ? value.trim().toLowerCase().replace(/[_-]+/g, ' ') : '';
  if (v === 'fixed' || v === 'fix') return 'fixed';
  if (v === 'not real' || v === 'notreal' || v === 'not a bug' || v === 'rejected' || v === 'invalid' || v === 'false positive') return 'not real';
  if (v === 'disputed' || v === 'dispute' || v === 'unsure' || v === 'needs person') return 'disputed';
  return null;
}

/**
 * The block's JSON, checked and trimmed; null when it isn't usable. An empty findings list is usable:
 * the review found nothing. A finding with no verdict it understands counts as disputed, so nothing
 * slips through as fixed.
 */
export function parseFactoryReviewJson(raw: string): FactoryReview | null {
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return null;
  }
  const obj = (Array.isArray(data) ? { findings: data } : data) as Record<string, unknown> | null;
  if (!obj || typeof obj !== 'object' || !Array.isArray(obj.findings)) return null;
  const findings: ReviewFinding[] = [];
  // Entries that can't be kept (no title, or past the cap) mustn't hide a dispute: they become one disputed line.
  let unread = Math.max(0, obj.findings.length - (FACTORY_REVIEW_MAX_FINDINGS - 1));
  const kept = unread ? obj.findings.slice(0, FACTORY_REVIEW_MAX_FINDINGS - 1) : obj.findings;
  for (const item of kept) {
    const f = (typeof item === 'string' ? { title: item } : (item ?? {})) as Record<string, unknown>;
    const title = text(f.title ?? f.finding, TITLE_MAX);
    if (!title) {
      unread++;
      continue;
    }
    const verdict = normalizeVerdict(f.verdict ?? f.status) ?? 'disputed';
    const where = text(f.where ?? f.file, WHERE_MAX);
    const reason = text(f.reason ?? f.note ?? f.why, REASON_MAX);
    const suggestion = verdict === 'disputed' ? text(f.suggestion, 60) : '';
    const task = verdict === 'disputed' ? taskRef(f.task) : '';
    findings.push({
      title,
      ...(where ? { where } : {}),
      verdict,
      ...(reason ? { reason } : {}),
      ...(verdict === 'fixed' && f.small === true ? { small: true } : {}),
      ...(suggestion ? { suggestion } : {}),
      ...(task ? { task } : {}),
      ...(verdict === 'disputed' ? cleanDisputeDetail(f) : {}),
    });
  }
  if (unread) {
    findings.push({
      title: `${unread} more ${unread === 1 ? 'entry' : 'entries'} in the builder's list couldn't be read`,
      verdict: 'disputed',
      reason: 'Untitled, or past the 30 the factory keeps; check the PR\'s "Codex review" section.',
    });
  }
  return { findings };
}

/** The last factory-review block in a message (the builder's own, after any it quoted); null when there's none, or when that last one is broken (never an earlier, quoted one). */
export function findFactoryReview(message: string): FactoryReview | null {
  let rest = message.replace(/\r\n?/g, '\n');
  let found: FactoryReview | null = null;
  for (let guard = 0; guard < 20; guard++) {
    const block = findFencedBlock(rest, FACTORY_REVIEW_LANG);
    if (!block) break;
    found = parseFactoryReviewJson(block.body);
    rest = rest.slice(block.end);
  }
  return found;
}

export interface ReviewCounts {
  fixed: number;
  not_real: number;
  disputed: number;
}

export function countFindings(findings: ReviewFinding[]): ReviewCounts {
  return {
    fixed: findings.filter((f) => f.verdict === 'fixed').length,
    not_real: findings.filter((f) => f.verdict === 'not real').length,
    disputed: findings.filter((f) => f.verdict === 'disputed').length,
  };
}

/** A fix that wasn't small: worth a second round of review. */
export const hasLargeFixes = (findings: ReviewFinding[]) => findings.some((f) => f.verdict === 'fixed' && !f.small);

/** Who reviewed: codex, cursor, or claude (through a T3 thread, when Codex built it). */
export const REVIEWERS = ['codex', 'cursor', 'claude'] as const;
export type Reviewer = (typeof REVIEWERS)[number];

export const REVIEWER_LABELS: Record<Reviewer, string> = {
  codex: 'Codex',
  cursor: 'Cursor CLI',
  claude: 'Claude',
};

/** "Codex (gpt-6.1-sol)", "Cursor CLI", … */
export function reviewerLabel(reviewer: string | null | undefined, model?: string | null): string {
  const name = reviewer && (REVIEWERS as readonly string[]).includes(reviewer) ? REVIEWER_LABELS[reviewer as Reviewer] : reviewer || 'A reviewer';
  return model ? `${name} (${model})` : name;
}

/** "Codex (gpt-6.1-sol)", or "Codex (gpt-6.1-sol) on box2" when another fleet machine reviewed (SF-T-104). */
export function reviewerWhereLabel(reviewer: string | null | undefined, model?: string | null, machine?: string | null): string {
  const label = reviewerLabel(reviewer, model);
  return machine ? `${label} on ${machine}` : label;
}

/**
 * The review on a task's pull request (TaskPr.review): the newest round's outcome, written by the
 * factory only. `needs_person` when anything is disputed or the builder didn't say what it did.
 * SF-T-74: once a person settles the disputed findings, `disputed` goes to 0 and they count as `left`
 * (left as the builder says) or `sent_back` (the builder was asked to fix them).
 */
export interface TaskPrReview extends ReviewCounts {
  reviewer: Reviewer | string;
  model: string | null;
  rounds: number;
  run_number: number;
  needs_person: boolean;
  at: string;
  left: number;
  sent_back: number;
  /** A review the builder didn't report on, once settled: the PR accepted as it is, or the builder asked for its verdict. */
  settled_as: 'accepted' | 'asked' | null;
  /** SF-T-104: another fleet machine gave the review (its name); absent or null: the run's own machine. */
  machine?: string | null;
}

export type ReviewBadgeInput = Pick<TaskPrReview, 'fixed' | 'not_real' | 'disputed' | 'needs_person'> & Partial<Pick<TaskPrReview, 'left' | 'sent_back' | 'settled_as'>>;

/**
 * "Reviewed: 3 fixed, 1 not real", "Reviewed: no findings", "Needs settling: 1 disputed", and once
 * settled (SF-T-74) "Reviewed: 3 fixed, 1 left as is" or "Reviewed: 3 fixed, 1 sent back".
 */
export function reviewBadgeText(r: ReviewBadgeInput): string {
  if (r.disputed > 0) return `Needs settling: ${r.disputed} disputed`;
  if (r.needs_person) return 'Needs settling: review unclear';
  if (r.settled_as === 'accepted') return 'Reviewed: accepted as is';
  if (r.settled_as === 'asked') return 'Reviewed: verdict asked for';
  const parts = [
    r.fixed ? `${r.fixed} fixed` : null,
    r.not_real ? `${r.not_real} not real` : null,
    r.left ? `${r.left} left as is` : null,
    r.sent_back ? `${r.sent_back} sent back` : null,
  ].filter(Boolean);
  return `Reviewed: ${parts.length ? parts.join(', ') : 'no findings'}`;
}

/** A stored review, checked; null when it isn't one. */
export function cleanTaskPrReview(raw: unknown): TaskPrReview | null {
  const r = raw as Partial<TaskPrReview> | null;
  if (!r || typeof r !== 'object') return null;
  const n = (v: unknown) => (typeof v === 'number' && Number.isInteger(v) && v >= 0 ? v : 0);
  if (typeof r.reviewer !== 'string' || typeof r.at !== 'string') return null;
  return {
    reviewer: r.reviewer.slice(0, 40),
    model: typeof r.model === 'string' ? r.model.slice(0, 100) : null,
    fixed: n(r.fixed),
    not_real: n(r.not_real),
    disputed: n(r.disputed),
    rounds: Math.max(1, n(r.rounds)),
    run_number: n(r.run_number),
    needs_person: r.needs_person === true,
    at: r.at.slice(0, 40),
    left: n(r.left),
    sent_back: n(r.sent_back),
    settled_as: r.settled_as === 'accepted' || r.settled_as === 'asked' ? r.settled_as : null,
    ...(typeof r.machine === 'string' && r.machine.trim() ? { machine: r.machine.trim().slice(0, 100) } : {}),
  };
}

/** A machine that can give a second-model review: Codex or the Cursor CLI installed and signed in. */
export function machineCanReview(reviewers: MachineReviewers | null | undefined): boolean {
  return !!reviewers && ((reviewers.codex.installed && reviewers.codex.logged_in === true) || (reviewers.cursor.installed && reviewers.cursor.logged_in === true));
}
